import { useState, type ReactNode } from "react";
import {
  SearchInput,
  SEARCH_INPUT_VARIANT,
} from "@/core/components/search-input";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import { useGetManagedPackThreadGroups } from "@/payroll/pack-chat/manager-query";
import { ManagerStartThreadForm } from "@/payroll/pack-chat/manager-start-thread-form";
import { PackChatConversation } from "@/payroll/pack-chat/pack-chat-conversation";
import { PackChatPackRail } from "@/payroll/pack-chat/pack-chat-pack-rail";
import { PackChatRefreshError } from "@/payroll/pack-chat/pack-chat-refresh-error";
import { usePackChatSection } from "@/payroll/pack-chat/pack-chat-section";
import {
  PackChatConversationSkeleton,
  PackChatListSkeleton,
  PackChatPackSkeleton,
} from "@/payroll/pack-chat/pack-chat-skeletons";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { packChatFirstThreadId } from "@/payroll/pack-chat/technician-groups";
import { isTemporaryPackChatError } from "@/payroll/pack-chat/thread-error-copy";
import { PackChatTechnicianRow } from "./pack-chat-technician-row";
import {
  PackChatThreadFilter,
  THREAD_FILTER,
  type ThreadFilter,
} from "./pack-chat-thread-filter";
import { PackChatThreadRow } from "./pack-chat-thread-row";

/**
 * Which columns the pane is showing.
 *
 * `list` is the list and one wide pane, for the start form and the empty states.
 * `conversation` adds the pack. `queue` adds the technician's questions between
 * the two, and only when there is more than one to choose between.
 */
/**
 * The list column is the SAME width in all three, and the pack column likewise, so
 * the only thing that ever moves between them is the queue appearing and the
 * conversation giving up its width for it. They used to disagree by 20-40px, which
 * shifted the list sideways on load and again on selecting a technician.
 */
// Written out rather than composed: Tailwind scans for whole class strings, so a
// template literal produces no CSS at all. The list column is minmax(240px,280px)
// and the pack minmax(280px,330px) in every one of them.
const PANE_COLUMNS = {
  /** Starting a thread: the form and the pack, and nothing else. */
  form: "md:grid-cols-1",
  list: "md:grid-cols-[minmax(240px,280px)_minmax(0,1fr)]",
  conversation:
    "md:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] min-[1440px]:grid-cols-[minmax(240px,280px)_minmax(0,1fr)_minmax(280px,330px)]",
  queue:
    "md:grid-cols-[minmax(240px,280px)_minmax(190px,230px)_minmax(0,1fr)] min-[1440px]:grid-cols-[minmax(240px,280px)_minmax(190px,230px)_minmax(0,1fr)_minmax(280px,330px)]",
} as const;

interface PackChatPanelProps {
  businessId: string | null;
  periodStart: string | undefined;
  /**
   * The run whose workbook this page is showing.
   *
   * Every pack read here uses it, rather than the run each question happens to be
   * pinned to: a thread is about a technician's pay for the PERIOD, and after a
   * rerun the pinned run's pack is a workbook nobody is working in. Pinned, two of
   * one technician's questions showed gross earnings $1,366 apart.
   */
  runId: string | null;
  /** A locked period takes the technicians' route in, so the empty state stops
      offering it. Managers keep theirs. */
  isLocked?: boolean;
}

/**
 * PackChat as columns: technicians, their questions, the conversation, the pack.
 *
 * It replaced a version that stacked a technician's questions as folds in the
 * reading pane. Giving the questions a column makes moving between two of them a
 * click rather than a fold and an unfold, and it leaves the conversation a pane
 * of its own — which is what let it become a chat, with a header and a reply box
 * that stay put while the messages scroll.
 */
export const PackChatPanel = ({
  businessId,
  periodStart,
  runId,
  isLocked = false,
}: PackChatPanelProps) => {
  // The start-form flag is driven from the page header, so it lives in the
  // section provider that spans both. Everything below is the panel's own.
  const { starting, stopStarting } = usePackChatSection();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ThreadFilter>(THREAD_FILTER.all);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<
    string | null
  >(null);
  const [openedThreadId, setOpenedThreadId] = useState<string | null>(null);

  // One unfiltered fetch, shaped in the query layer: the pills need counts across
  // the whole period while the list needs the filtered threads collapsed into
  // technicians, and both fall out of the same cached response.
  const listQuery = useGetManagedPackThreadGroups(
    businessId,
    periodStart ?? null,
    { status: filter === THREAD_FILTER.all ? undefined : filter, search },
  );
  const counts = listQuery.data?.counts;
  const groups = listQuery.data?.groups ?? [];

  // Keep the active conversation selected when server priority changes.
  const activeTechnicianId =
    !starting &&
    groups.some((group) => group.technician.id === selectedTechnicianId)
      ? selectedTechnicianId
      : (groups[0]?.technician.id ?? null);
  const activeGroup = groups.find(
    (group) => group.technician.id === activeTechnicianId,
  );
  const activeThreads = activeGroup?.threads ?? [];
  const openThreadId = activeThreads.some(
    (thread) => thread.id === openedThreadId,
  )
    ? openedThreadId
    : (packChatFirstThreadId(activeThreads) ?? activeThreads[0]?.id ?? null);
  const openThread =
    activeThreads.find((thread) => thread.id === openThreadId) ?? null;

  // Record fallback selections before rendering children, so refreshes cannot
  // redirect a draft. Filters can still select the first remaining conversation.
  if (
    !starting &&
    activeTechnicianId &&
    openThreadId &&
    (selectedTechnicianId !== activeTechnicianId ||
      openedThreadId !== openThreadId)
  ) {
    setSelectedTechnicianId(activeTechnicianId);
    setOpenedThreadId(openThreadId);
  }
  const refreshFailed = Boolean(
    listQuery.data && isTemporaryPackChatError(listQuery.error),
  );

  // `md:h-full` so the columns inside can resolve a height against it: without
  // it they measured against an auto-height wrapper and grew to their content,
  // which is how the grid ended up twice the height of the card holding it.
  const shell = (children: ReactNode) => (
    <Stack
      fullWidth
      gap="md"
      align="stretch"
      className="min-w-0 md:h-full md:min-h-0"
    >
      {refreshFailed && (
        <PackChatRefreshError onRetry={() => void listQuery.refetch()} />
      )}
      {children}
    </Stack>
  );

  if (!periodStart) return shell(null);

  // The list column and the pane beside it, in the shape they will land in, rather
  // than one spinner floating in the middle of the whole tab.
  if (listQuery.isLoading) {
    return shell(
      <Stack
        gap="none"
        align="stretch"
        className={cn(
          "grid min-w-0 md:h-full",
          PANE_COLUMNS.conversation,
          "duration-200 animate-in fade-in",
        )}
      >
        <Stack
          gap="none"
          align="stretch"
          className="min-h-0 min-w-0 md:h-full md:border-r md:border-stroke-tertiary"
        >
          <PackChatListSkeleton />
        </Stack>
        <Stack
          gap="none"
          align="stretch"
          className="hidden min-h-0 min-w-0 md:block md:h-full"
        >
          <PackChatConversationSkeleton />
        </Stack>
        <Stack
          gap="none"
          align="stretch"
          className="hidden min-h-0 min-w-0 border-l border-stroke-tertiary min-[1440px]:block min-[1440px]:h-full"
        >
          <PackChatPackSkeleton />
        </Stack>
      </Stack>,
    );
  }

  if (listQuery.isError && !refreshFailed) {
    return shell(
      <PackChatState
        fill
        error={listQuery.error}
        title="PackChat couldn't be loaded"
        body="Try again. If it keeps failing, contact support."
        action={
          <Button
            variant="secondary"
            size="sm"
            loading={listQuery.isFetching}
            className="mt-2"
            onClick={() => void listQuery.refetch()}
          >
            Try again
          </Button>
        }
      />,
    );
  }

  // Nothing has ever been asked in this period: four empty columns would say the
  // same thing four times.
  if (counts?.all === 0 && !starting) {
    return shell(
      <PackChatState
        fill
        title="No questions this period"
        body={
          isLocked
            ? "This pay period is locked, so technicians can no longer raise one. You can still start a thread yourself from New thread above."
            : "Technicians can raise a question from any figure on their PayPack. You can start one yourself from New thread above."
        }
      />,
    );
  }

  const nothingMatches = groups.length === 0 && !starting;
  // Neither the queue nor the pack earns space over the start form or an empty
  // result.
  const reading = Boolean(!starting && !nothingMatches && activeGroup);
  // One question is not a queue. A column holding a single row repeats what the
  // conversation beside it already says, so the conversation takes the width
  // instead and the column comes back when there is a choice to make.
  const showThreads = reading && activeThreads.length > 1;
  const showPack = reading && Boolean(openThread);

  return shell(
    // Fills the card at md+, which the tab sizes against the viewport, so each
    // column scrolls on its own the way an inbox does. It has to resolve to a
    // definite height rather than a `max-height`: a percentage height resolves
    // against the parent's *specified* height, so under `max-h` a scroll viewport
    // grows to its content and gets clipped by `overflow: hidden` — rows below
    // the fold become unreachable rather than scrollable. Below md the columns
    // stack and flow with the page.
    <Stack
      gap="none"
      align="stretch"
      className={cn(
        // No gap: the columns are divided by hairlines and own their gutters, so
        // each one's scrollbar lands on a real edge.
        "grid min-w-0 md:h-full",
        {
          [PANE_COLUMNS.form]: starting,
          [PANE_COLUMNS.queue]: !starting && showThreads,
          [PANE_COLUMNS.conversation]: !starting && !showThreads && showPack,
          [PANE_COLUMNS.list]: !starting && !showThreads && !showPack,
        },
      )}
    >
      {/* The list's controls live in the list's column, so the conversation
          starts at the top of the card rather than under a control row it has
          nothing to do with. Search is the column's own top bar — a hairline
          under it does the framing a bordered box was doing badly — and the
          filters read as a heading on the rows they filter.

          Hidden while a thread is being started: picking a figure off a pack is
          the whole screen, and a queue of other people's questions beside it is
          both a distraction and 280px the form and the pack could use. Cancel is
          the way back. */}
      <Stack
        gap="none"
        align="stretch"
        className={cn("min-h-0 min-w-0 md:border-r md:border-stroke-tertiary", {
          hidden: starting,
        })}
      >
        <Stack
          direction="row"
          gap="sm"
          align="center"
          className="shrink-0 border-b border-stroke-tertiary px-5 py-2"
        >
          <SearchInput
            variant={SEARCH_INPUT_VARIANT.bare}
            value={search}
            onChange={setSearch}
            placeholder="Search threads"
          />
        </Stack>
        <Stack gap="none" align="stretch" className="shrink-0 px-5 pb-2 pt-3">
          <PackChatThreadFilter
            value={filter}
            counts={{
              [THREAD_FILTER.all]: counts?.all ?? 0,
              [THREAD_FILTER.open]: counts?.open ?? 0,
              [THREAD_FILTER.closed]: counts?.closed ?? 0,
            }}
            onChange={setFilter}
          />
        </Stack>
        <ScrollArea vertical className="min-h-0 w-full flex-1">
          {/* The inset rides inside the scroll, like the other panes: the rows
              then run to the card's edge as you scroll and still clear it at the
              end of the list. */}
          <Stack
            fullWidth
            gap="none"
            className="min-w-0 divide-y divide-stroke-tertiary pb-5"
          >
            {groups.map((group) => (
              <PackChatTechnicianRow
                key={group.technician.id}
                group={group}
                selected={
                  !starting && group.technician.id === activeTechnicianId
                }
                onSelect={() => {
                  stopStarting();
                  setSelectedTechnicianId(group.technician.id);
                  // Drop the pinned question: this technician's queue decides
                  // what opens, not whichever thread the last one left behind.
                  setOpenedThreadId(null);
                }}
              />
            ))}
          </Stack>
        </ScrollArea>
      </Stack>

      {showThreads && (
        <Stack
          gap="none"
          align="stretch"
          className="min-h-0 min-w-0 md:border-r md:border-stroke-tertiary"
        >
          <ScrollArea vertical className="h-full min-h-0">
            {/* Named, because on its own a column of figures beside a column of
                people does not say whose questions these are. */}
            <Stack
              fullWidth
              gap="none"
              role="group"
              aria-label={`Questions from ${activeGroup?.technician.name ?? "this technician"}`}
              className="min-w-0 divide-y divide-stroke-tertiary pb-5"
            >
              {activeThreads.map((thread) => (
                <PackChatThreadRow
                  key={thread.id}
                  thread={thread}
                  selected={thread.id === openThreadId}
                  onSelect={() => setOpenedThreadId(thread.id)}
                />
              ))}
            </Stack>
          </ScrollArea>
        </Stack>
      )}

      {activeGroup && openThread && !starting && !nothingMatches ? (
        // The conversation owns this column: its header and its reply box stay
        // put, and only the messages scroll.
        <Stack gap="none" align="stretch" className="min-h-0 min-w-0">
          <PackChatConversation
            key={openThread.id}
            businessId={businessId}
            periodStart={periodStart}
            runId={runId}
            thread={openThread}
            technicianName={activeGroup.technician.name}
            onReplied={setOpenedThreadId}
          />
        </Stack>
      ) : (
        <ScrollArea vertical className="h-full min-h-0 min-w-0">
          {/* The start form lays out its own two panes to the pane edge and owns
              its padding; the states either side of it stay a readable column. */}
          <Stack
            gap="none"
            align="stretch"
            className={cn({
              "min-w-0": starting,
              // `h-full` so a state that fills the pane has a height to
              // resolve against; the start form keeps its own top alignment.
              "h-full max-w-[46rem] px-5 py-5": !starting,
            })}
          >
            {nothingMatches ? (
              <PackChatState
                fill
                title={
                  search.trim()
                    ? "No threads match that search"
                    : `Nothing ${filter}`
                }
                body={
                  search.trim()
                    ? `No thread in this period mentions "${search.trim()}".`
                    : "Every question in this period is under one of the other filters."
                }
                action={
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-3"
                    onClick={() => {
                      setSearch("");
                      setFilter(THREAD_FILTER.all);
                    }}
                  >
                    Show all threads
                  </Button>
                }
              />
            ) : starting ? (
              <ManagerStartThreadForm
                businessId={businessId}
                periodStart={periodStart}
                activeRunId={runId}
                onCancel={stopStarting}
                onStarted={(thread) => {
                  setFilter(THREAD_FILTER.all);
                  setSelectedTechnicianId(thread.technician.id);
                  setOpenedThreadId(thread.id);
                  stopStarting();
                }}
              />
            ) : (
              <PackChatState
                fill
                title="Nothing selected"
                body="Pick a technician on the left to read their questions."
              />
            )}
          </Stack>
        </ScrollArea>
      )}

      {showPack && activeGroup && openThread && (
        <Stack
          gap="none"
          align="stretch"
          className="hidden h-full min-h-0 min-w-0 border-l border-stroke-tertiary min-[1440px]:flex"
        >
          <PackChatPackRail
            businessId={businessId}
            runId={runId ?? openThread.ai_script_run_id}
            technicianId={activeGroup.technician.id}
            threads={activeThreads}
            openThreadId={openThreadId}
            onSelectThread={setOpenedThreadId}
          />
        </Stack>
      )}
    </Stack>,
  );
};
